import { Component } from '@angular/core';
import { Contact, ContactResponse } from '../../../services/http/contacts/contacts.interface';
import { ContactsService } from '../../../services/http/contacts/contacts.service';
import { ActivatedRoute, Router } from '@angular/router';
import { LoaderService } from '../../../services/loader/loader.service';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { OperatorsService } from '../../../services/http/operators/operators.service';
import { Operator } from '../../../services/http/operators/operators.interface';
import { AlertService } from '../../../services/alert/alert.service';
import { AlertType } from '../../../services/alert/alert.interface';
import { AuthService } from '../../../services/auth/auth.service';
import { AttachmentUtil } from '../../../services/utils/attachment.utils';
import { CustomInputFileComponent } from '../../../components/custom-input-file/custom-input-file.component';
import { DownloadFileService } from '../../../services/downloadFile/download-file.service';
import { AllAttachment, Attachment } from '../../../services/http/attachments/attachments.interface';

@Component({
  selector: 'app-contact-detail',
  standalone: true,
  imports: [FormsModule, CommonModule, CustomInputFileComponent],
  templateUrl: './contact-detail.component.html',
  styleUrl: './contact-detail.component.scss'
})
export class ContactDetailComponent {
  id: string
  contact: Contact | undefined;
  files: File[] = [];

  operators: Operator[] = [];

  resetFiles = false;

  constructor(
    private readonly contactsService: ContactsService,
    private readonly activeRoute: ActivatedRoute,
    private readonly loaderService: LoaderService,
    private readonly router: Router,
    private readonly operatorsService: OperatorsService,
    private readonly alertService: AlertService,
    private readonly authService: AuthService,
    private readonly downloadFileService: DownloadFileService
  ) {
    this.id = this.activeRoute.snapshot.paramMap.get('id') || '';
    if (!this.id) {
      this.router.navigate(['/contacts']);
    }
    this.loaderService.show();
    this.operatorsService.getAllOperators().subscribe(
      (operators: Operator[]) => {
        this.operators = operators;
      }
    );
    this.contactsService.getContact(this.id).subscribe(
      (contact: Contact) => {
        if (contact.response == undefined || contact.response.length == 0) {
          contact.response = [];
          contact.response.push({
            comments: '',
            officialResponse: '',
            operator: '',
            attachments: []
          });
        }
        this.contact = contact;
        this.loaderService.hide();
      }
    );
  }

  translateType(type: string) {
    switch (type) {
      case 'Complaint':
        return $localize`:@@ContacDetail.Complaint:Queja`;
      case 'Suggestion':
        return $localize`:@@ContacDetail.Suggestion:Sugerencia`;
      case 'Information':
        return $localize`:@@ContacDetail.Information:Petición de información`;
      case 'Other':
        return $localize`:@@ContacDetail.Other:Otro`;
      default:
        return type;
    }
  }


  // Método para manejar la selección de archivos
  onFileChange(event: File | null): void {
    if (event) {
      this.files = [event];
    }
  }

  getOperatorName(operator: string) {
    const operatorObj = this.operators.find((op) => op.identifier === operator);
    return operatorObj ? operatorObj.name : operator;
  }

  downloadFile(fileItem: Attachment | undefined) {
    if (fileItem && this.contact) {
      //window.open(AttachmentUtil.getDirectLinkContactPrivate(this.contact.identifier.toString(), fileItem.uuid!));
      this.downloadFileService.getAttachementNewWindow(AttachmentUtil.getDirectLinkContactPrivate(this.contact.identifier.toString(), fileItem.uuid!));
    }
  }


  save() {
    if (this.contact && !this.contact.response[0].comments) {
      this.alertService.setAlert({
        message: $localize`:@@ContactDetail.CommentRequired:El comentario es obligatorio`,
        type: AlertType.Warning,
        dismissible: true,
        dismissOnTimeout: 2000
      });
    }
    this.loaderService.show();
    var data = {
      comments: this.contact?.response[0].comments,
      officialResponse: "",
      operator: this.authService.getCurrentUser().documentNumber,
      attachments: []
    } as ContactResponse;
    if (this.files.length > 0) {
      const filePromises = this.files.map(async (file) => {
        const reader = new FileReader();
        return new Promise<void>((resolve, reject) => {
          reader.onload = async () => {
            try {
              data.attachments.push({
                name: file.name,
                description: '',
                mime: file.type,
                data: await AttachmentUtil.arrayBufferToString(file.arrayBuffer())
              });
              resolve(); // Marca este archivo como procesado
            } catch (error) {
              reject(error); // Maneja errores de forma individual
            }
          };
          reader.onerror = (error) => reject(error); // Maneja errores del lector
          reader.readAsDataURL(file);
        });
      });

      // Espera a que todos los archivos se procesen
      Promise.all(filePromises)
        .then(() => {
          // Llama a la función de añadir comentario
          this.contactsService.addCommentToContact(this.id, 'Revision', data).subscribe(() => {
            this.contactsService.getContact(this.id).subscribe(
              (contact: Contact) => {
                this.contact = contact;
                this.resetFiles = true;
                this.files = [];
                setTimeout(() => {
                  this.resetFiles = false;
                }, 100);
                this.loaderService.hide();
              }
            );
          });
        })
        .catch((error) => {
          console.error('Error al procesar archivos:', error);
          this.loaderService.hide(); // Esconde el loader en caso de error
        });
    } else {
      this.contactsService.addCommentToContact(this.id, 'Revision', data).subscribe(() => {
        this.contactsService.getContact(this.id).subscribe(
          (contact: Contact) => {
            this.contact = contact;
            this.resetFiles = true;
            this.files = [];
            setTimeout(() => {
              this.resetFiles = false;
            }, 100);
            this.loaderService.hide();
          }
        );
      });
    }
  }

  close() {
    // same as save but set officialResponse
    if (this.contact && !this.contact.response[0].officialResponse) {
      this.alertService.setAlert({
        message: $localize`:@@ContactDetail.ResponseRequired:La respuesta oficial es obligatoria`,
        type: AlertType.Warning,
        dismissible: true,
        dismissOnTimeout: 2000
      });
    }
    this.loaderService.show();
    var data = {
      comments: this.contact?.response[0].comments,
      officialResponse: this.contact?.response[0].officialResponse,
      operator: this.authService.getCurrentUser().documentNumber,
      attachments: [],
    } as ContactResponse;
    if (this.files.length > 0) {
      const filePromises = this.files.map(async (file) => {
        const reader = new FileReader();
        return new Promise<void>((resolve, reject) => {
          reader.onload = async () => {
            try {
              data.attachments.push({
                name: file.name,
                description: '',
                mime: file.type,
                data: await AttachmentUtil.arrayBufferToString(file.arrayBuffer())
              });
              resolve(); // Marca este archivo como procesado
            } catch (error) {
              reject(error); // Maneja errores de forma individual
            }
          };
          reader.onerror = (error) => reject(error); // Maneja errores del lector
          reader.readAsDataURL(file);
        });
      });

      // Espera a que todos los archivos se procesen
      Promise.all(filePromises)
        .then(() => {
          // Llama a la función de añadir comentario
          this.contactsService.addCommentToContact(this.id, 'Closed', data).subscribe(() => {
            this.contactsService.getContact(this.id).subscribe(
              (contact: Contact) => {
                this.contact = contact;
                this.resetFiles = true;
                this.files = [];
                setTimeout(() => {
                  this.resetFiles = false;
                }, 100);
                this.loaderService.hide();
              }
            );
          });
        })
        .catch((error) => {
          console.error('Error al procesar archivos:', error);
          this.loaderService.hide(); // Esconde el loader en caso de error
        });
    } else {
      this.contactsService.addCommentToContact(this.id, 'Closed', data).subscribe(() => {
        this.contactsService.getContact(this.id).subscribe(
          (contact: Contact) => {
            this.contact = contact;
            this.resetFiles = true;
            this.files = [];
            setTimeout(() => {
              this.resetFiles = false;
            }, 100);
            this.loaderService.hide();
          }
        );
      });
    }
  }

  goBack() {
    this.router.navigate(['/contacts']);
  }
}
